// Renders the platform-wide banner a superadmin publishes from
// SuperadminDashboard's Announcement tab (see migration 0104) -- e.g.
// scheduled maintenance -- above the normal admin/driver dashboards.
// Nothing rendered at all while there's no active message, so this is a
// no-op for every tenant on every normal day.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { AlertTriangle, Info, Megaphone } from 'lucide-react';

interface Announcement {
  message: string | null;
  severity: 'info' | 'warning' | 'critical';
  is_active: boolean;
}

const SEVERITY_STYLES: Record<Announcement['severity'], string> = {
  info: 'border-blue-900/40 bg-blue-950/60 text-blue-300',
  warning: 'border-yellow-900/40 bg-yellow-950/60 text-yellow-300',
  critical: 'border-red-900/40 bg-red-950/60 text-red-300',
};

const SEVERITY_ICONS: Record<Announcement['severity'], React.ElementType> = {
  info: Info,
  warning: Megaphone,
  critical: AlertTriangle,
};

const PlatformAnnouncementBanner: React.FC = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    supabase
      .from('platform_announcement')
      .select('message, severity, is_active')
      .eq('id', true)
      .maybeSingle()
      .then(({ data }) => setAnnouncement(data as Announcement | null));
  }, []);

  if (!announcement?.is_active || !announcement.message) return null;

  const Icon = SEVERITY_ICONS[announcement.severity];
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2.5 text-sm border-b ${SEVERITY_STYLES[announcement.severity]}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <p>{announcement.message}</p>
    </div>
  );
};

export default PlatformAnnouncementBanner;
